/// <reference path="../../../../WebUI/typings/tsd.d.ts" />

import * as React from "react";
import * as ReactDOM from "react-dom";

import Rest = require("../../Generated/RestClient");

import { Resources } from "../../Resources/Intercom.Resources.DevPortal";

import { DevPortalAppBase } from "../../DevPortalAppBase";
import { clientAppSettings } from "../../Models/ClientAppSettings";
import logger from "../../../../Intercom.ReactUI/Utils/logger";
import { AzurePortalMessenger, InfoResult, AccessTokensResult, EndpointStatusResult, DefaultPortalSignature } from "../Utility/AzurePortalMessenger";
import { AuthenticationUtility } from "../Utility/AuthenticationUtility";

import { StateStore } from "../Store/StateStore";
import { Actions } from "../Actions/Actions";
import { Reducers } from "../Reducers/Reducers";

import { StateStoreUtility } from "../Store/StateStoreUtility";

import { PageStatusFlags, IPageStatus } from "../ComponentDataModels/IPageStatus";

import { IbizaWebChatPage, IbizaWebChatPageProps } from "./../Views/IbizaWebChatPage";

/**
 * Page loading status.
 */
export class PageStatus implements IPageStatus {
    private _pageStatus: PageStatusFlags = PageStatusFlags.Ready;

    public constructor(pageStatus: PageStatusFlags) {
        this._pageStatus = pageStatus;
    }

    public hasPageStatusFlag(flag: PageStatusFlags): boolean {
        return (this._pageStatus & flag) === flag;
    }

    public isReady(): boolean {
        return this._pageStatus === PageStatusFlags.Ready;
    }
}

/**
 * Web Chat controller.
 * This is used by the Ibiza Azure portal.
 */
export class WebChatController extends SimpleMvc.Controller {
    private _azurePortalMessenger: AzurePortalMessenger;
    private _onReceiveInfo: (info: InfoResult) => void;
    private _onReceiveAzureToken: (tokens: AccessTokensResult) => void;
    private _onReceiveEndpointStatus: (status: EndpointStatusResult) => void;

    private _reactComponent: IbizaWebChatPage;

    //#region - Component props/store - these could move out of here but the component bootstraps things for now.

    private _store: StateStore;
    private _actions: Actions;

    //#endregion - Component props/store

    constructor(public app: DevPortalAppBase) {
        super();
    }

    /**
     * See SimpleMvc.Controller.load
     */
    public load(params: SimpleMvc.QueryParams): void {
        super.load(params);
        const botId = params["id"];
        const location = params["location"];
        if (!botId) {
            throw new Error("Bot id is required.");
        }

        // Initialize state store and wire up component actions.
        this._store = { botId: botId, location: location, pageStatus: PageStatusFlags.LoggingIn | PageStatusFlags.WaitingForBotReady | PageStatusFlags.WaitingForWebChatToken };
        this._actions = { onWebChatNewConversation: () => { this._onWebChatNewConversation(); } };

        // todo: Parse locale here instead of in DevPortalAppBase ctor.

        this._createAzurePortalMessenger();
        this._initializePageDependencies();

        this._render();
    }

    /**
     * See SimpleMvc.Controller.unload
     */
    public unload(): void {
        this._teardownAzureMessenger();
        ReactDOM.unmountComponentAtNode(this.app.getAppBody());
        super.unload();
    }

    private _render(): void {
        // Overwrite the app body content with new content.
        const props = StateStoreUtility.mapStateAndDispatchToProps(this._store, this._actions);

        const element = React.createElement(IbizaWebChatPage, props);
        this._reactComponent = ReactDOM.render<IbizaWebChatPageProps, IbizaWebChatPage>(element, this.app.getAppBody());
    }

    private _createAzurePortalMessenger(): void {
        if (this._azurePortalMessenger) {
            throw new Error("_azurePortalMessenger already created");
        }

        // Azure always sends us a trusted authority for x-frame messages. If we don't get this then error out.
        const trustedAuthorityMatches = decodeURIComponent(window.location.search).match(/trustedAuthority=([^&]+)/);
        if (!trustedAuthorityMatches) {
            throw new Error("Unsupported URI - missing trustedAuthority");
        }

        logger.debug("WebChatController: Starting to listen to Azure cross frame messages");
        const trustedAuthority = trustedAuthorityMatches[1];
        
        const signatureMatches = decodeURIComponent(window.location.search).match(/framesignature=([^&]+)/);
        let signature = DefaultPortalSignature;
        if (signatureMatches) {
            signature = signatureMatches[1];
        }

        this._azurePortalMessenger = new AzurePortalMessenger(trustedAuthority, signature);
    }

    private _initializePageDependencies(): void {
        if (!this._azurePortalMessenger) {
            throw new Error("Must initialize azure portal messenger.");
        }

        // Get the version info from our Ibiza blade.
        this._onReceiveInfo = this._onReceiveInfoFromIFrame.bind(this);
        this._azurePortalMessenger.listenForInfo(this._onReceiveInfo);

        // Log the user in and listen for auth token changes - these can be refreshed periodically without corresponding requestAuthenticationTokens calls.
        this._onReceiveAzureToken = this._onReceiveAuthenticationTokensFromIFrame.bind(this);
        this._azurePortalMessenger.listenForAuthenticationTokens(this._onReceiveAzureToken);

        // Want to wait until we get a "bot ready" message from Azure. This lets us know the bot is ready to receive messages.
        //  Note: In reality Azure sends this when a ping returns or after a timeout - so we are not assured the bot is ready.
        this._onReceiveEndpointStatus = this._onReceiveEndpointStatusFromIFrame.bind(this);
        this._azurePortalMessenger.listenForEndpointStatus(this._onReceiveEndpointStatus);

        // Tell Azure we are ready to get messages.
        this._azurePortalMessenger.postReady();
        this._azurePortalMessenger.initializationComplete();

        // Request all the info we need and trigger auto-refresh of that data.
        this._azurePortalMessenger.requestInfo();
        this._azurePortalMessenger.requestAuthenticationTokens();
        this._azurePortalMessenger.requestEndpointStatus();
    }

    private _teardownAzureMessenger(): void {
        if (this._onReceiveInfo) {
            this._azurePortalMessenger.unlistenForInfo(this._onReceiveInfo);
            this._onReceiveInfo = undefined;
        }
        if (this._onReceiveAzureToken) {
            this._azurePortalMessenger.unlistenForAuthenticationTokens(this._onReceiveAzureToken);
            this._onReceiveAzureToken = undefined;
        }
        if (this._onReceiveEndpointStatus) {
            this._azurePortalMessenger.unlistenForEndpointStatus(this._onReceiveEndpointStatus);
            this._onReceiveEndpointStatus = undefined;
        }
        if (this._azurePortalMessenger) {
            this._azurePortalMessenger = undefined;
        }
    }

    private _getWebChatToken(): void {
        Rest.BotManagerClient.getWebChatToken(this._store.botId).done(token => {
            if (!this.isLoaded()) {
                return;
            }

            logger.debug("WebChatController: Got WebChat token");

            this._store = Reducers.getWebChatTokenCompleted(this._store, token);
            this._render();
        }).fail( (error) => {
            if (!this.isLoaded()) {
                return;
            }

            logger.debug("WebChatController: Failed to get web chat token. " + error);

            this._store = Reducers.getWebChatTokenFailed(this._store, Resources.getChatTokenFailure);
            this._render();
        });
    }

    //#region - Actions

    /**
     * Starts a new conversation - clears the current web chat token and gets a new one.
     */
    private _onWebChatNewConversation() {
        this._store = Reducers.clearWebChatTokenAndUpdateState(this._store);
        this._getWebChatToken();
        this._render();
    }

    //#endregion - Actions

    //#region - Azure portal message handlers.

    /**
     * Callback for Info message from Azure portal.
     */
    private _onReceiveInfoFromIFrame(info: InfoResult): void {
        if (!this.isLoaded()) {
            return;
        }

        // We can do something here in the future based on the version... for now just output the value.
        logger.debug("WebChatController: Got Azure Extension version: " + info.version);
        this._azurePortalMessenger.unlistenForInfo(this._onReceiveInfo);
        this._onReceiveInfo = undefined;
    }

    private _handleFailedAuthRefresh() {
        this.app.signedInUser = null;
        this._store = Reducers.loginFailed(this._store, Resources.signInFailure);
        this._render();
    }

    /**
     * Callback whenever we get authentication tokens from the Azure portal. This refreshes the local tokens/user info.
     */
    private _onReceiveAuthenticationTokensFromIFrame(tokens: AccessTokensResult): void {
        if (!this.isLoaded()) {
            return;
        }

        const armToken = tokens.armToken;
        const selfToken = tokens.selfToken;

        const useSelfToken = selfToken && clientAppSettings.flightedFeatures.indexOf("firstPartyAuth") >= 0;
        const token = useSelfToken ? selfToken : armToken;
        logger.debug("WebChatController: Azure sent us Authentication tokens - Refreshing.");

        const csrfToken = AuthenticationUtility.getCsrfToken();
        if (!csrfToken) {
            logger.debug("WebChatController: Failed getting csrfToken from cookie");
            this._handleFailedAuthRefresh();
            return;
        }
        $.ajax("/refreshLogin", {
            data: { token, '__RequestVerificationToken': csrfToken },
            type: "POST"
        })
        .then(() => {
            if (!this.isLoaded()) {
                return;
            }

            Rest.ajaxHeaders = _.extend(Rest.ajaxHeaders || {}, { Authorization: `Bearer ${token}`});
            // Get the signed in user.
            logger.debug("WebChatController: Getting signed in user.");
            return Rest.AccountClient.getSignedInUser();
        })
        .then((authData: Rest.AuthData) => {
            if (!this.isLoaded()) {
                return;
            }

            logger.debug("WebChatController: Got authenticated user.");

            if (!this.app.signedInUser) {
                this.app.signedInUser = authData;
            }

            AuthenticationUtility.bindAzureAccessToken(this.app.signedInUser, armToken, true);

            this._store = Reducers.loginComplete(this._store);
            this._render();

            // If we don't have a web chat token yet kick off the process to get one.
            if (!this._store.webChatToken) {
                this._getWebChatToken();
            }
        })
        .fail(xhr => {
            if (!this.isLoaded()) {
                return;
            }

            logger.debug("WebChatController: Failed authorization web call:" + xhr);
            this._handleFailedAuthRefresh();
        });
    }

    private _onReceiveEndpointStatusFromIFrame(status: EndpointStatusResult): void {
        if (!this.isLoaded()) {
            return;
        }

        logger.debug("WebChatController: Got new bot status from Azure: " + status.status);

        switch (status.status) {
            case "not-ready":
                this._store = Reducers.onBotEndpointNotReady(this._store);
                break;
            case "ready":
            case "no-endpoint":
            case "error":
            default:
                this._store = Reducers.onBotEndpointReady(this._store);
                break;
        }

        this._render();
    }

    //#endregion - Azure portal message handlers
}



// WEBPACK FOOTER //
// ./Client/v4/Controllers/WebChatController.ts